import { cx } from "@emotion/css";
import { Chip, CircularProgress } from "@mui/material";
import { Search, X } from "lucide-react";
import type { Ref } from "react";
import { useTranslation } from "react-i18next";

export const isMac = /Mac|iPhone|iPad/.test(navigator.userAgent);

type SearchInputProps = {
	value: string;
	onChange: (value: string) => void;
	onClear: () => void;
	onFocus?: () => void;
	placeholder: string;
	isLoading?: boolean;
	showShortcut?: boolean;
	autoFocus?: boolean;
	// zawężenie wyników pokazane w polu (np. wybrane pytanie), ✕ je zdejmuje
	chip?: { label: string; onRemove: () => void };
	className?: string;
	inputRef?: Ref<HTMLInputElement>;
};

export function SearchInput({
	value,
	onChange,
	onClear,
	onFocus,
	placeholder,
	isLoading,
	showShortcut,
	autoFocus,
	chip,
	className,
	inputRef,
}: SearchInputProps) {
	const { t } = useTranslation();

	return (
		<div className={cx("search-input", className)}>
			{isLoading ? (
				<CircularProgress className="search-input__icon search-input__spinner" />
			) : (
				<Search className="search-input__icon icon-small" />
			)}
			{chip && (
				<Chip
					size="small"
					label={chip.label}
					onDelete={chip.onRemove}
					deleteIcon={<X className="icon-super-small" />}
					className="search-input__chip"
					data-test-id="search-input-chip"
				/>
			)}
			<input
				ref={inputRef}
				type="text"
				// biome-ignore lint/a11y/noAutofocus: modal szukajki ma od razu przyjmować tekst
				autoFocus={autoFocus}
				autoComplete="off"
				value={value}
				placeholder={placeholder}
				onChange={(e) => onChange(e.target.value)}
				onFocus={onFocus}
				onKeyDown={(e) => e.key === "Escape" && e.currentTarget.blur()}
				className="search-input__field"
				data-test-id="global-search-input"
			/>
			{value || chip ? (
				<button
					type="button"
					onClick={() => {
						onClear();
						chip?.onRemove();
					}}
					aria-label={t("globalSearch.clear", "Wyczyść")}
					className="search-input__clear"
					data-test-id="global-search-clear"
				>
					<X className="icon-super-small" />
				</button>
			) : (
				showShortcut && (
					<>
						<span className="search-input__hint search-input__hint--idle">
							<kbd>{isMac ? "⌘" : "Ctrl"}</kbd>
							<kbd>K</kbd>
						</span>
						<span className="search-input__hint search-input__hint--focus">
							<kbd>Esc</kbd>
						</span>
					</>
				)
			)}
		</div>
	);
}
